<script>
    import { createEventDispatcher } from "svelte";
    import { Button } from "../../lib/ui";
    import {
        STAGE_ADVANCE_TYPES,
        advanceTypeOf,
        STAGE_PARAMS,
        BOSS_MOVES,
        BOSS_STATS,
        BOSS_SLOT_KINDS,
        VOLLEY_PATTERNS,
        VOLLEY_SPEC,
        newStage,
        newBoss,
        newVolley,
    } from "./gameSlots.js";

    /** 이 맵의 스테이지 목록. 비면 맵이 통째로 한 판이다(예전 동작). */
    export let stages = [];
    /** 고를 수 있는 편대(= 이 맵이 쓰는 편대). */
    export let patterns = [];
    /** assetId → URL. 보스 썸네일용(저장하지 않는다). */
    export let urls = {};

    const dispatch = createEventDispatcher();
    const emit = (next) => dispatch("change", next);

    const updateAt = (i, patch) => emit(stages.map((st, k) => (k === i ? { ...st, ...patch } : st)));
    const updateBoss = (i, patch) =>
        updateAt(i, { boss: { ...(stages[i].boss || newBoss()), ...patch } });
    const updateParam = (i, key, value) =>
        updateAt(i, { params: { ...(stages[i].params || {}), [key]: value } });
    const resetParam = (i, key) => {
        const params = { ...(stages[i].params || {}) };
        delete params[key];
        updateAt(i, { params });
    };

    const addStage = () => emit([...stages, newStage(stages.length)]);
    const removeStage = (i) => emit(stages.filter((_, k) => k !== i));
    const move = (i, dir) => {
        const to = i + dir;
        if (to < 0 || to >= stages.length) return;
        const next = [...stages];
        [next[i], next[to]] = [next[to], next[i]];
        emit(next);
    };

    const togglePattern = (i, pid) => {
        const cur = stages[i].patternIds || [];
        updateAt(i, {
            patternIds: cur.includes(pid) ? cur.filter((p) => p !== pid) : [...cur, pid],
        });
    };

    const changeAdvanceType = (i, type) =>
        updateAt(i, { advance: { type, value: advanceTypeOf(type).def } });

    // ── 탄막 ──
    const volleysOf = (st) => st.boss?.volleys || [];
    const updateVolley = (i, vi, patch) =>
        updateBoss(i, { volleys: volleysOf(stages[i]).map((v, k) => (k === vi ? { ...v, ...patch } : v)) });
    const addVolley = (i) => updateBoss(i, { volleys: [...volleysOf(stages[i]), newVolley()] });
    const removeVolley = (i, vi) =>
        updateBoss(i, { volleys: volleysOf(stages[i]).filter((_, k) => k !== vi) });

    const num = (v, def) => (typeof v === "number" && Number.isFinite(v) ? v : def);
    const statOf = (boss, spec) => num(boss?.[spec.key], spec.def);
    const paramOf = (st, spec) => num(st.params?.[spec.key], spec.def);
    const fmtParam = (spec, v) => (spec.kind === "delta" ? (v > 0 ? `+${v}` : String(v)) : `×${v.toFixed(1)}`);
</script>

<div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">스테이지</div>
<div class="tw-mb-3 tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
    맵 안의 <strong class="tw-text-foreground">단계</strong>입니다. 화면 위 배지가 이 번호를 보여주므로
    플레이어에겐 곧 <strong class="tw-text-foreground">“얼마나 왔나”</strong>가 됩니다.
    아무것도 안 만들면 이 맵은 예전처럼 <strong class="tw-text-foreground">한 덩어리</strong>로 돕니다.
    난이도는 <strong class="tw-text-foreground">[맵] 값 위에 또 곱해집니다</strong> — 뒤 단계일수록 크게 두세요.
</div>

{#each stages as st, i (st.id)}
    {@const adv = advanceTypeOf(st.advance?.type)}
    <div class="stage-card">
        <div class="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span class="tw-rounded-full tw-bg-primary/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-bold tw-text-primary">
                STAGE {i + 1}
            </span>
            <input
                class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="단계 이름 (선택 — 화면 가운데 알림에 뜹니다)"
                value={st.name || ""}
                on:change={(e) => updateAt(i, { name: e.target.value })}
            />
            <button type="button" class="mini-btn" title="위로" on:click={() => move(i, -1)}>↑</button>
            <button type="button" class="mini-btn" title="아래로" on:click={() => move(i, 1)}>↓</button>
            <button type="button" class="mini-btn hover:tw-border-destructive hover:tw-text-destructive"
                on:click={() => removeStage(i)}>삭제</button>
        </div>

        <!-- 다음 단계로 넘어가는 조건 -->
        <div class="tw-mb-4 tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">다음 단계 조건</span>
                <select
                    class="tw-h-9 tw-w-[130px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                    value={adv.value}
                    on:change={(e) => changeAdvanceType(i, e.target.value)}
                >
                    {#each STAGE_ADVANCE_TYPES as t (t.value)}
                        <option value={t.value}>{t.label}</option>
                    {/each}
                </select>
            </label>
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-text-muted-foreground">{adv.hint}</span>
                <span class="tw-flex tw-items-center tw-gap-2">
                    <input
                        class="tw-h-9 tw-w-[130px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="number" min={adv.min} max={adv.max}
                        value={num(st.advance?.value, adv.def)}
                        on:change={(e) =>
                            updateAt(i, {
                                advance: {
                                    type: adv.value,
                                    value: Math.min(adv.max, Math.max(adv.min, Number(e.target.value) || adv.def)),
                                },
                            })}
                    />
                    <span class="tw-text-sm tw-text-muted-foreground">{adv.unit}</span>
                </span>
            </label>
        </div>

        <!-- 이 단계의 편대 -->
        <div class="tw-mb-4">
            <div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">이 단계에 나오는 편대</div>
            <div class="tw-mb-2 tw-text-xs tw-text-muted-foreground">
                안 고르면 <strong class="tw-text-foreground">이 맵이 쓰는 편대 전부</strong>가 나옵니다.
            </div>
            {#if patterns.length === 0}
                <div class="tw-text-xs tw-text-muted-foreground">[적 편대] 탭에서 만들면 여기서 고를 수 있어요.</div>
            {:else}
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                    {#each patterns as p (p.id)}
                        {@const on = (st.patternIds || []).includes(p.id)}
                        <button type="button"
                            class="chip {on ? 'tw-border-primary tw-bg-primary/10 tw-text-primary' : 'tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-text-foreground'}"
                            on:click={() => togglePattern(i, p.id)}>{p.name || "이름 없음"}</button>
                    {/each}
                </div>
            {/if}
        </div>

        <!-- 난이도 보정 -->
        <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-3">
            {#each STAGE_PARAMS as spec (spec.key)}
                {@const v = paramOf(st, spec)}
                <div>
                    <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <span class="tw-text-[13px] tw-text-foreground">{spec.label}</span>
                        <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {v !== spec.def ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                            {fmtParam(spec, v)}
                            {#if v !== spec.def}
                                <button type="button" class="link-btn" on:click={() => resetParam(i, spec.key)}>되돌리기</button>
                            {/if}
                        </span>
                    </div>
                    <input class="tw-w-full" style="accent-color: hsl(var(--sb-primary))" type="range"
                        min={spec.min} max={spec.max} step={spec.step} value={v}
                        on:change={(e) => updateParam(i, spec.key, Number(e.target.value))} />
                </div>
            {/each}
        </div>

        <!-- 보스 -->
        <div class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-p-4">
            <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                <input type="checkbox" style="accent-color: hsl(var(--sb-primary))"
                    checked={!!st.boss}
                    on:change={(e) => updateAt(i, { boss: e.target.checked ? newBoss() : null })} />
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">이 단계 끝에 보스</span>
            </label>
            <div class="tw-mt-1 tw-text-xs tw-text-muted-foreground">
                조건을 채우면 편대가 멈추고 보스가 내려옵니다. 잡거나 제한 시간이 지나야 다음 단계로 갑니다.
            </div>

            {#if st.boss}
                <div class="tw-mt-4 tw-flex tw-flex-col tw-gap-4">
                    <input
                        class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="text" placeholder="보스 이름 (선택 — 등장할 때 뜹니다)"
                        value={st.boss.name || ""}
                        on:change={(e) => updateBoss(i, { name: e.target.value })} />

                    <!-- 그림 -->
                    <div class="tw-flex tw-flex-wrap tw-gap-4">
                        {#each BOSS_SLOT_KINDS as kind (kind.field)}
                            <div class="tw-flex tw-w-[104px] tw-flex-col tw-items-center tw-gap-1">
                                <button type="button"
                                    class="slot-thumb {st.boss[kind.field] && urls[st.boss[kind.field]] ? 'tw-border-border' : 'tw-border-dashed tw-border-border'}"
                                    on:click={() => dispatch("pick", { index: i, field: kind.field })}>
                                    {#if st.boss[kind.field] && urls[st.boss[kind.field]]}
                                        <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain"
                                            src={urls[st.boss[kind.field]]} alt="" />
                                    {:else}
                                        <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                                    {/if}
                                </button>
                                <div class="tw-text-[12px] tw-font-semibold tw-text-foreground">{kind.label}</div>
                                {#if st.boss[kind.field]}
                                    <button type="button" class="link-btn" on:click={() => updateBoss(i, { [kind.field]: null })}>기본으로</button>
                                {:else}
                                    <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">{kind.hint}</div>
                                {/if}
                            </div>
                        {/each}
                    </div>

                    <!-- 능력치 -->
                    <div class="tw-flex tw-flex-wrap tw-gap-4">
                        {#each BOSS_STATS as spec (spec.key)}
                            <label class="tw-flex tw-flex-col tw-gap-1.5">
                                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">{spec.label}</span>
                                <input
                                    class="tw-h-9 tw-w-[130px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                                    type="number" min={spec.min} max={spec.max} step={spec.step}
                                    value={statOf(st.boss, spec)}
                                    on:change={(e) =>
                                        updateBoss(i, {
                                            [spec.key]: Math.min(spec.max, Math.max(spec.min, Number(e.target.value) || spec.def)),
                                        })} />
                                {#if spec.hint}
                                    <span class="tw-max-w-[200px] tw-text-[11px] tw-leading-tight tw-text-muted-foreground">{spec.hint}</span>
                                {/if}
                            </label>
                        {/each}
                        <label class="tw-flex tw-flex-col tw-gap-1.5">
                            <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">움직임</span>
                            <select
                                class="tw-h-9 tw-w-[150px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                                value={st.boss.move || "sweep"}
                                on:change={(e) => updateBoss(i, { move: e.target.value })}>
                                {#each BOSS_MOVES as m (m.value)}
                                    <option value={m.value}>{m.label}</option>
                                {/each}
                            </select>
                            <span class="tw-max-w-[200px] tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                                {BOSS_MOVES.find((m) => m.value === (st.boss.move || "sweep"))?.hint}
                            </span>
                        </label>
                    </div>

                    <!-- 탄막 -->
                    <div>
                        <div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">탄막</div>
                        <div class="tw-mb-2 tw-text-xs tw-text-muted-foreground">
                            위에서부터 <strong class="tw-text-foreground">차례로 돌아가며</strong> 쏩니다.
                            하나도 없으면 탄을 안 쏘고 몸으로만 막습니다.
                        </div>
                        {#each volleysOf(st) as v, vi (vi)}
                            <div class="tw-mb-2 tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-rounded-md tw-border tw-border-border tw-bg-background tw-p-3">
                                <label class="tw-flex tw-flex-col tw-gap-1">
                                    <span class="tw-text-[12px] tw-text-muted-foreground">모양</span>
                                    <select
                                        class="tw-h-8 tw-w-[120px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                                        value={v.pattern}
                                        on:change={(e) => updateVolley(i, vi, { pattern: e.target.value })}>
                                        {#each VOLLEY_PATTERNS as vp (vp.value)}
                                            <option value={vp.value}>{vp.label}</option>
                                        {/each}
                                    </select>
                                </label>
                                {#each VOLLEY_SPEC as spec (spec.key)}
                                    <label class="tw-flex tw-flex-col tw-gap-1">
                                        <span class="tw-text-[12px] tw-text-muted-foreground">{spec.label}</span>
                                        <input
                                            class="tw-h-8 tw-w-[92px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                                            type="number" min={spec.min} max={spec.max} step={spec.step}
                                            value={num(v[spec.key], spec.def)}
                                            on:change={(e) =>
                                                updateVolley(i, vi, {
                                                    [spec.key]: Math.min(spec.max, Math.max(spec.min, Number(e.target.value) || spec.def)),
                                                })} />
                                    </label>
                                {/each}
                                <button type="button" class="mini-btn hover:tw-border-destructive hover:tw-text-destructive"
                                    on:click={() => removeVolley(i, vi)}>삭제</button>
                            </div>
                        {/each}
                        <Button variant="outline" size="sm" on:click={() => addVolley(i)}>+ 탄막 추가</Button>
                    </div>
                </div>
            {/if}
        </div>
    </div>
{/each}

<div class="tw-mt-2">
    <Button variant="outline" on:click={addStage}>+ 스테이지 추가</Button>
</div>

<style>
    .stage-card {
        margin-bottom: 14px;
        padding: 16px;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 12px;
    }
    .mini-btn {
        height: 36px;
        padding: 0 12px;
        min-width: 36px;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 6px;
        background: hsl(var(--sb-background));
        color: hsl(var(--sb-muted-foreground));
        font-size: 12px;
        cursor: pointer;
    }
    .mini-btn:hover { color: hsl(var(--sb-foreground)); }
    .chip {
        padding: 6px 12px;
        border-width: 1px;
        border-style: solid;
        border-radius: 9999px;
        font-size: 12px;
        cursor: pointer;
        transition: background-color 0.15s;
    }
    .link-btn {
        border: 0;
        background: transparent;
        color: hsl(var(--sb-muted-foreground));
        font-size: 11px;
        text-decoration: underline;
        cursor: pointer;
    }
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다. */
    .slot-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 104px;
        height: 104px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }
    .slot-thumb:hover { border-color: hsl(var(--sb-primary)); }
</style>
